import React, { useState, useRef, useEffect } from 'react';
import { Button, Alert, Spinner, Card, CardHeader, CardBody, FormGroup, Label, Input, Row, Col, Badge } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';
import { formatBytes } from '../../helpers/formatHelpers';

export const AdminExportImport = () => {
  const [backupLoading, setBackupLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [backupInfo, setBackupInfo] = useState(null);
  const uploadFileInputRef = useRef(null);

  useEffect(() => {
    loadBackupInfo();
  }, []);

  const loadBackupInfo = async () => {
    try {
      const info = await apiClient.getBackupInfo();
      setBackupInfo(info);
    } catch (exc) {
      console.log('No backup file found');
      setBackupInfo(null);
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm('Queue a backup export job? The export will run in the background.')) {
      return;
    }

    try {
      setBackupLoading(true);
      setError(null);
      setSuccess(null);

      await apiClient.createBackup();
      setSuccess('Backup export job queued. Check Scheduler Logs for progress.');
    } catch (exc) {
      console.error('Failed to queue backup job:', exc);
      setError(exc.message || 'Failed to queue backup job');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDownloadBackup = () => {
    try {
      setError(null);
      apiClient.downloadBackup();
    } catch (exc) {
      console.error('Failed to download backup:', exc);
      setError(exc.message || 'Failed to download backup');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const handleUploadBackup = async () => {
    const file = uploadFileInputRef.current?.files?.[0];
    if (!file) {
      setError('Please select a file to upload');
      return;
    }

    if (!file.name.endsWith('.tar.gz') && !file.name.endsWith('.tgz')) {
      setError('Invalid file format. Please select a .tar.gz or .tgz file');
      return;
    }

    if (!window.confirm('Upload this file to storage as the current backup? It will replace any existing backup file.')) {
      return;
    }

    try {
      setUploadLoading(true);
      setError(null);
      setSuccess(null);

      const result = await apiClient.uploadBackup(file);
      setSuccess('Backup file uploaded to storage successfully.');
      setBackupInfo({
        exists: true,
        sizeBytes: result.sizeBytes,
        lastModified: result.lastModified,
        etag: result.etag,
        kind: result.kind,
        uploadedByUserId: result.uploadedByUserId,
        uploadedByUserName: result.uploadedByUserName
      });
      if (uploadFileInputRef.current) {
        uploadFileInputRef.current.value = '';
      }
    } catch (exc) {
      console.error('Failed to upload backup:', exc);
      setError(exc.message || 'Failed to upload backup');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteBackup = async () => {
    if (!window.confirm('Delete the backup file and its metadata from storage? This cannot be undone.')) {
      return;
    }

    try {
      setDeleteLoading(true);
      setError(null);
      setSuccess(null);

      await apiClient.deleteBackup();
      setBackupInfo(null);
      setSuccess('Backup file deleted from storage.');
    } catch (exc) {
      console.error('Failed to delete backup:', exc);
      setError(exc.message || 'Failed to delete backup');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleImport = async () => {
    if (!backupInfo?.exists) {
      setError('No backup file in storage. Upload or generate a backup first.');
      return;
    }

    if (!window.confirm('Queue a backup import job? This will replace all data in the database with the backup from storage. The import runs in the background.')) {
      return;
    }

    try {
      setImportLoading(true);
      setError(null);
      setSuccess(null);

      await apiClient.importData();
      setSuccess('Backup import job queued. Check Scheduler Logs for progress.');
    } catch (exc) {
      console.error('Failed to queue import job:', exc);
      setError(exc.message || 'Failed to queue import job');
    } finally {
      setImportLoading(false);
    }
  };

  const kindBadge = (kind) => {
    if (kind === 'Uploaded') return <Badge color="warning" pill>Uploaded</Badge>;
    return <Badge color="success" pill>Generated</Badge>;
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Export / Import Data</h2>
      </div>

      <Alert color="danger" className="mb-3" isOpen={!!error} toggle={() => setError(null)} fade={false}>
        <strong>Error:</strong> {error}
      </Alert>

      <Alert color="success" className="mb-3" isOpen={!!success} toggle={() => setSuccess(null)} fade={false}>
        <strong>Success:</strong> {success}
      </Alert>

      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <CardHeader>
              <h5 className="mb-0">Backup in Storage</h5>
            </CardHeader>
            <CardBody>
              {backupInfo?.exists ? (
                <div className="mb-3 p-3 bg-light rounded">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <div><strong>Size:</strong> {formatBytes(backupInfo.sizeBytes)}</div>
                      <div><strong>Last Modified:</strong> {formatDate(backupInfo.lastModified)}</div>
                      <div><strong>Kind:</strong> {kindBadge(backupInfo.kind)}</div>
                      {backupInfo.uploadedByUserName && (
                        <div><strong>Uploaded by:</strong> {backupInfo.uploadedByUserName}</div>
                      )}
                      <div className="text-muted small mt-1">ETag: {backupInfo.etag}</div>
                    </div>
                    <Button
                        color="outline-danger"
                        size="sm"
                        onClick={handleDeleteBackup}
                        disabled={deleteLoading || backupLoading || uploadLoading || importLoading}
                      >
                        {deleteLoading ? <Spinner size="sm" /> : 'Delete'}
                      </Button>
                  </div>
                </div>
              ) : (
                <Alert color="info" className="mb-3" fade={false}>
                  No backup file in storage.
                </Alert>
              )}

              <div className="d-flex gap-2 mb-2">
                <Button color="outline-secondary" size="sm" onClick={loadBackupInfo}>
                  Refresh
                </Button>
              </div>

              {backupInfo?.exists && (
                <Button
                  color="success"
                  onClick={handleDownloadBackup}
                  disabled={backupLoading || uploadLoading || importLoading || deleteLoading}
                >
                  Download Backup
                </Button>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Card className="mb-3">
            <CardHeader>
              <h5 className="mb-0">Export Data</h5>
            </CardHeader>
            <CardBody>
              <p className="text-muted">
                Queue a background job to export all data to storage as a backup (kind: Generated).
                Monitor progress in Scheduler Logs.
              </p>
              <Button
                color="primary"
                onClick={handleCreateBackup}
                disabled={backupLoading || uploadLoading || importLoading || deleteLoading}
              >
                {backupLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Queueing...
                  </>
                ) : (
                  'Start Export Job'
                )}
              </Button>
            </CardBody>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="mb-3">
            <CardHeader>
              <h5 className="mb-0">Upload Backup to Storage</h5>
            </CardHeader>
            <CardBody>
              <p className="text-muted">
                Upload a previously exported .tar.gz file to storage (kind: Uploaded), then use it for import.
              </p>
              <FormGroup>
                <Label for="uploadFile">Select TAR.GZ file</Label>
                <Input
                  type="file"
                  id="uploadFile"
                  innerRef={uploadFileInputRef}
                  accept=".tar.gz,.tgz"
                  disabled={uploadLoading}
                />
              </FormGroup>
              <Button
                color="warning"
                onClick={handleUploadBackup}
                disabled={backupLoading || uploadLoading || importLoading || deleteLoading}
              >
                {uploadLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  'Upload to Storage'
                )}
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col md={12}>
          <Card className="mb-3">
            <CardHeader>
              <h5 className="mb-0">Import Data</h5>
            </CardHeader>
            <CardBody>
              <p className="text-muted">
                Queue a background job to import data from the backup currently in storage.
                This will replace all data in the database. Monitor progress in Scheduler Logs.
              </p>
              {!backupInfo?.exists && (
                <Alert color="warning" className="mb-3" fade={false}>
                  No backup file in storage. Generate an export or upload a backup file first.
                </Alert>
              )}
              <Button
                color="danger"
                onClick={handleImport}
                disabled={!backupInfo?.exists || backupLoading || uploadLoading || importLoading || deleteLoading}
              >
                {importLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Queueing...
                  </>
                ) : (
                  'Start Import Job'
                )}
              </Button>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};
